import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Cloud, 
  Code, 
  Database, 
  Globe, 
  Layers, 
  Layout, 
  MessageSquare, 
  Plus, 
  Rocket, 
  Search, 
  ShieldCheck, 
  Slack, 
  Github,
  Zap,
  CheckCircle2,
  Terminal,
  Cpu,
  Mail,
  Box,
  Share2,
  Lock,
  Key,
  ShieldAlert,
  Calendar,
  Hourglass,
  Sparkles,
  Server,
  Wrench,
  HeartHandshake,
  UserCheck,
  FileText,
  Activity,
  GitBranch,
  BellRing,
  ArrowLeftRight,
  RefreshCw,
  Workflow,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Settings,
  Shield
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const categories = [
  { id: "all", label: "All Integrations" },
  { id: "crm", label: "CRM & Sales" },
  { id: "dev", label: "Developer Tools" },
  { id: "comm", label: "Communication" },
  { id: "storage", label: "Cloud Storage" },
  { id: "automation", label: "Automation" }
];

const integrations = [
  {
    name: "Salesforce",
    category: "crm",
    desc: "Sync project tasks with Salesforce leads and opportunities automatically.",
    icon: Cloud,
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    name: "GitHub",
    category: "dev",
    desc: "Link pull requests and commits directly to PMS cards.",
    icon: Github,
    color: "bg-gray-50",
    iconColor: "text-gray-900"
  },
  {
    name: "Slack",
    category: "comm",
    desc: "Get real-time project updates and task alerts directly in your Slack channels.",
    icon: Slack,
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  },
  {
    name: "Google Drive",
    category: "storage",
    desc: "Attach documents and manage permissions directly from your boards.",
    icon: Database,
    color: "bg-yellow-50",
    iconColor: "text-yellow-600"
  },
  {
    name: "Microsoft Teams",
    category: "comm",
    desc: "Collaborate on tasks without leaving your Teams workspace.",
    icon: MessageSquare,
    color: "bg-indigo-50",
    iconColor: "text-indigo-600"
  },
  {
    name: "Docker",
    category: "dev",
    desc: "Monitor deployment status and container health within project logs.",
    icon: Box,
    color: "bg-blue-50",
    iconColor: "text-blue-500"
  },
  {
    name: "Gemini AI",
    category: "dev",
    desc: "Power your workflow with intelligent task extraction and automation.",
    icon: Sparkles,
    color: "bg-sky",
    iconColor: "text-navy"
  },
  {
    name: "AWS",
    category: "storage",
    desc: "Securely manage cloud resources and infrastructure credentials.",
    icon: Layers,
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  },
  {
    name: "Zapier",
    category: "automation",
    desc: "Connect PMS to 5,000+ apps with custom no-code zaps.",
    icon: Zap,
    color: "bg-orange-50",
    iconColor: "text-orange-500"
  },
  {
    name: "GitLab",
    category: "dev",
    desc: "Enterprise-grade CI/CD and source control integration.",
    icon: Code,
    color: "bg-orange-50",
    iconColor: "text-orange-700"
  },
  {
    name: "Discord",
    category: "comm",
    desc: "Rich webhooks and bot notifications for community-driven teams.",
    icon: MessageSquare,
    color: "bg-blue-50",
    iconColor: "text-indigo-500"
  },
  {
    name: "Dropbox",
    category: "storage",
    desc: "Secure file sharing and versioning for design and creative assets.",
    icon: Box,
    color: "bg-blue-50",
    iconColor: "text-blue-700"
  }
];

const securityFeatures = [
  {
    title: "OAuth 2.0 Standard",
    desc: "We never store your passwords. Every integration uses secure OAuth protocols.",
    icon: Key
  },
  {
    title: "Data Encryption",
    desc: "All data synced between tools is encrypted in transit and at rest.",
    icon: Lock
  },
  {
    title: "Granular Permissions",
    desc: "Control exactly what each integrated tool can access on your boards.",
    icon: ShieldCheck
  },
  {
    title: "Audit Logging",
    desc: "Every automated action is logged for full transparency and compliance.",
    icon: ShieldAlert
  }
];

const setupSteps = [
  {
    title: "Choose Your App",
    desc: "Select from our extensive library of certified integrations.",
    icon: Search
  },
  {
    title: "Authenticate",
    desc: "Securely connect your account with a single click via OAuth.",
    icon: UserCheck
  },
  {
    title: "Configure & Go",
    desc: "Set your preferences and watch your data sync in real-time.",
    icon: Zap
  }
];

const integrationFaqs = [
  {
    q: "How often does data sync between PMS and other apps?",
    a: "Most integrations, like Slack and GitHub, are real-time via webhooks. For CRM tools like Salesforce, sync occurs every 5-10 minutes depending on your plan level."
  },
  {
    q: "Can I build my own custom integration?",
    a: "Absolutely. We provide a full REST API and a JavaScript SDK. Our Developer Portal has all the documentation you need to get started."
  },
  {
    q: "Is there a limit to the number of integrations I can use?",
    a: "Standard plans include up to 5 active integrations. Enterprise plans offer unlimited connectivity and access to premium Salesforce features."
  },
  {
    q: "Are the integrations secure?",
    a: "Yes. All integrations use OAuth 2.0, meaning PMS never sees your third-party credentials. Data is encrypted using AES-256 standards."
  }
];

const upcomingIntegrations = [
  { name: "Jira Sync", eta: "comming soon", icon: Layers },
  { name: "Markup", eta: "comming soon", icon: Globe },
  { name: "Notion", eta: "comming soon", icon: FileText },
  { name: "Figma", eta: "comming soon", icon: Layout },
];

const IntegrationsPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const filteredIntegrations = useMemo(() => {
    return integrations.filter(item => {
      const matchesCategory = activeCategory === "all" || item.category === activeCategory;
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           item.desc.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-navy section-animate relative min-h-[70vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky rounded-full blur-[120px] -mr-80 -mt-80" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 rounded-full blur-[100px] -ml-20 -mb-20" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow !bg-white/10 !text-white !border-white/20">Connect Your Stack</span>
              <h1 className="text-white mt-6 mb-8">
                Seamless <span className="text-sky">Integrations</span> for modern teams.
              </h1>
              <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
                PMS connects with the tools you already use, ensuring your project data flows smoothly across your entire ecosystem.
              </p>
              <div className="flex justify-center gap-4">
                <a href="#browse" onClick={(e) => handleNavClick(e, "browse")} className="btn-secondary">
                  Browse Apps
                </a>
                <Link to="/contact" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                  Request an Integration
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Workflow Section */}
        <section className="shell-white py-24 border-b border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">How it works</span>
              <h2 className="mt-4 mb-6 text-navy">One Action, <span className="text-blue-600">Infinite Possibilities.</span></h2>
              <p className="text-xl text-gray-600">See how PMS orchestrates your daily operations across platforms.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 items-start">
              <div className="flex flex-col items-center text-center p-8 bg-gray-50 rounded-[40px] reveal stagger-1 text-left">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white mb-8 shadow-xl shadow-blue-200">
                  <Github className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Pull Request Opened</h3>
                <p className="text-gray-600">A developer creates a PR in GitHub. PMS immediately captures the metadata and links it to the relevant card.</p>
              </div>

              <div className="flex flex-col items-center text-center p-8 bg-blue-50 rounded-[40px] border-2 border-blue-200 shadow-2xl scale-105 relative reveal stagger-2">
                <div className="absolute -top-4 bg-navy text-sky px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">PMS Core</div>
                <div className="w-24 h-24 bg-navy rounded-[32px] flex items-center justify-center text-white mb-8">
                  <Cpu className="w-12 h-12 text-sky" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Automated Transition</h3>
                <p className="text-gray-700">The "AI Ticket Extractions" board automatically moves the task to "Review" and notifies the assigned QA lead.</p>
              </div>

              <div className="flex flex-col items-center text-center p-8 bg-gray-50 rounded-[40px] reveal stagger-3 text-left">
                <div className="w-20 h-20 bg-[#4A154B] rounded-full flex items-center justify-center text-white mb-8 shadow-xl shadow-purple-200">
                  <Slack className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Channel Alert</h3>
                <p className="text-gray-600">The #engineering Slack channel receives a rich preview notification with a direct link to the review board.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Browser Section */}
        <section id="browse" className="shell-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-16 reveal">
              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-6 py-2.5 rounded-full font-bold transition-all ${
                      activeCategory === cat.id
                        ? "bg-navy text-sky shadow-lg shadow-navy/20"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search integrations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-navy/10 transition-all"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 min-h-[400px]">
              {filteredIntegrations.length > 0 ? (
                filteredIntegrations.map((item, idx) => (
                  <div key={idx} className="group p-8 bg-white border border-gray-100 rounded-[32px] hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-500/5 transition-all reveal stagger-1">
                    <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                      <item.icon className={`w-8 h-8 ${item.iconColor}`} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-8">{item.desc}</p>
                    <button className="flex items-center gap-2 text-navy font-bold hover:text-blue-600 transition-colors">
                      Learn more
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-20 bg-gray-50 rounded-[40px] border-2 border-dashed border-gray-200">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900">No integrations found</h3>
                  <p className="text-gray-500 mt-2">Try adjusting your search or category filter.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Seamless Migration Section */}
        <section className="shell-white py-24 border-y border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left text-left">
                <span className="section-eyebrow">Switching is Easy</span>
                <h2 className="mt-4 mb-6 text-navy">Migrate from <span className="text-blue-600">Trello & Asana</span> in minutes.</h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-10">
                  Don't let legacy data hold you back. Our built-in migration tools ensure you can bring your entire project history into PMS with a few clicks.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Data Integrity</h4>
                      <p className="text-sm text-gray-500">We preserve every comment, attachment, and timestamp.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Custom Mapping</h4>
                      <p className="text-sm text-gray-500">Map your old columns to PMS workflows easily.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="reveal-right">
                <div className="bg-navy rounded-[40px] p-10 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10 z-0">
                    <Workflow className="w-32 h-32 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-8 text-left">One-Click Importer</h3>
                  <div className="space-y-4 z-50">
                    {['Trello Boards', 'Asana Projects', 'Jira Backlogs', 'CSV/Excel'].map((tool, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-white/10 rounded-2xl border border-white/10 group hover:bg-white/20 transition-all cursor-pointer">
                        <span className="font-bold">{tool}</span>
                        <div className="flex items-center gap-2 text-sky text-sm font-bold">
                          Import
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Integration - Salesforce */}
        <section className="shell-sky py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left text-left">
                <span className="section-eyebrow">Enterprise Power</span>
                <h2 className="mt-4 mb-6 text-navy">Built-in <span className="text-blue-600">Salesforce</span> Connectivity.</h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-10">
                  Leanport's deep expertise in Salesforce means PMS is more than just a PMS. It's a bridge between your sales pipeline and engineering delivery.
                </p>
                <div className="space-y-4">
                  {[
                    "Two-way sync between SFDC Cases and PMS Cards.",
                    "Automated task creation based on Opportunity stages.",
                    "Real-time visibility for account managers.",
                    "Custom object mapping supported."
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-blue-600" />
                      <span className="text-lg font-medium text-navy/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reveal-right">
                <div className="bg-white rounded-[40px] p-12 shadow-2xl relative overflow-hidden text-center border border-blue-100">
                  <div className="flex items-center justify-between mb-12">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center">
                      <Cloud className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1 mx-8 h-0.5 border-t-2 border-dashed border-gray-200 relative">
                      <Zap className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-yellow-500 fill-yellow-500" />
                    </div>
                    <div className="w-16 h-16 bg-navy rounded-2xl flex items-center justify-center">
                      <Box className="w-8 h-8 text-sky" />
                    </div>
                  </div>
                  <div className="space-y-4 text-left">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                      <span className="font-bold text-gray-900">New Deal Closed</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">Salesforce</span>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 flex items-center justify-between">
                      <span className="font-bold text-blue-900">Project Initialized</span>
                      <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full font-bold">TeamBoard</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NEW: Custom Enterprise Solutions (Leanport Highlight) */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center text-left">
              <div className="reveal-left order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -inset-4 bg-blue-50 rounded-[48px] -rotate-2" />
                  <div className="relative bg-white p-10 rounded-[40px] shadow-2xl border border-gray-100">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center text-sky">
                        <Settings className="w-6 h-6" />
                      </div>
                      <h3 className="text-2xl font-bold text-navy">Bespoke Engineering</h3>
                    </div>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      Need a custom bridge between PMS and your internal proprietary tools? Leanport Software provides dedicated engineering squads to build and maintain private integrations.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-sm font-bold text-navy">
                        <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        Dedicated Support Channel
                      </div>
                      <div className="flex items-center gap-3 text-sm font-bold text-navy">
                        <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        Custom Data Mapping & Transformation
                      </div>
                      <div className="flex items-center gap-3 text-sm font-bold text-navy">
                        <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        On-Premise Integration Options
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="reveal-right order-1 lg:order-2">
                <span className="section-eyebrow">Enterprise Services</span>
                <h2 className="mt-4 mb-6 text-navy">Tailored Connectivity by <span className="text-blue-600">Leanport.</span></h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-10">
                  Leveraging a decade of offshore excellence, our parent company Leanport Software Pvt. Ltd. builds the custom bridges that enterprise ecosystems require.
                </p>
                <Link to="/contact" className="btn-primary">
                  Speak with an Architect
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Powerful Ecosystem Details */}
        <section className="shell-white py-24 border-t border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">Deep Dives</span>
              <h2 className="mt-4 mb-6 text-navy">Built for <span className="text-blue-600">Your Workflow.</span></h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 text-left">
              <div className="p-10 bg-gray-50 rounded-[40px] border border-gray-100 reveal stagger-1">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600">
                    <Slack className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy">Slack for Teams</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Instant notifications for @mentions and assignments.",
                    "Create TeamBoard cards directly from any Slack message.",
                    "Weekly project summaries delivered to selected channels.",
                    "Rich link previews for all TeamBoard cards."
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-600">
                      <BellRing className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-10 bg-gray-50 rounded-[40px] border border-gray-100 reveal stagger-2">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center text-white">
                    <Github className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy">GitHub for Engineers</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Auto-sync branch names with PMS card IDs.",
                    "Update card status via commit message keywords.",
                    "View PR status and reviewer comments on the board.",
                    "Integrated deployment tracking for every task."
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-600">
                      <GitBranch className="w-5 h-5 text-gray-900 mt-1 flex-shrink-0" />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Setup Guide Section */}
        <section className="shell-sky py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20 reveal">
            <span className="section-eyebrow">Ease of Use</span>
            <h2 className="mt-4 mb-6 text-navy">Ready in <span className="text-blue-600">Minutes.</span></h2>
            <p className="text-xl text-gray-600">No coding required. Just select, sync, and succeed.</p>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {setupSteps.map((step, idx) => (
                <div key={idx} className="relative reveal stagger-1 text-center bg-white p-10 rounded-[40px] shadow-sm">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-8 text-blue-600 border-4 border-white shadow-xl">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-navy">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed px-4">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security & Trust Section */}
        <section className="shell-navy py-24 text-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left order-2 lg:order-1">
                <div className="grid grid-cols-2 gap-6">
                  {securityFeatures.map((feat, idx) => (
                    <div key={idx} className="p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 transition-all group text-left">
                      <div className="w-12 h-12 bg-sky rounded-lg flex items-center justify-center mb-4 text-navy group-hover:scale-110 transition-transform">
                        <feat.icon className="w-6 h-6" />
                      </div>
                      <h4 className="font-bold text-white mb-2">{feat.title}</h4>
                      <p className="text-xs text-blue-100 leading-relaxed">{feat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reveal-right order-1 lg:order-2 text-left">
                <span className="section-eyebrow !bg-white/10 !text-white !border-white/20">Enterprise Grade</span>
                <h2 className="mt-4 mb-6">Security you can <span className="text-sky">bank on.</span></h2>
                <p className="text-xl text-blue-100 leading-relaxed">
                  Integrating your stack shouldn't mean compromising your security. PMS is built with enterprise governance at its core.
                </p>
                <div className="mt-8 flex items-center gap-4 p-6 bg-white/10 rounded-2xl border border-white/10">
                  <ShieldCheck className="w-12 h-12 text-sky" />
                  <div>
                    <p className="font-bold text-white text-lg">SOC2 Type II Compliant</p>
                    <p className="text-sm text-blue-200">Verified security standards for all platform data.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Developer API Section */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center text-left">
              <div className="reveal-left order-2 lg:order-1">
                <div className="bg-navy rounded-[40px] p-8 shadow-2xl relative overflow-hidden border border-navy/20">
                  <div className="bg-[#0D0D0D] rounded-2xl p-6 font-mono text-sm text-sky/80 leading-relaxed shadow-inner">
                    <div className="flex gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <p><span className="text-purple-400">const</span> pms = <span className="text-purple-400">require</span>(<span className="text-green-400">'@pms/sdk'</span>);</p>
                    <p className="mt-2"><span className="text-gray-500">// Initialize client</span></p>
                    <p><span className="text-purple-400">const</span> client = pms.<span className="text-blue-400">init</span>(process.env.API_KEY);</p>
                    <p className="mt-4"><span className="text-gray-500">// Create automated board</span></p>
                    <p><span className="text-purple-400">await</span> client.boards.<span className="text-blue-400">create</span>(&#123;</p>
                    <p className="pl-4">name: <span className="text-green-400">'Auto-Sync Workflow'</span>,</p>
                    <p className="pl-4">visibility: <span className="text-green-400">'private'</span></p>
                    <p>&#125;);</p>
                  </div>
                </div>
              </div>
              <div className="reveal-right order-1 lg:order-2">
                <span className="section-eyebrow">Built for Builders</span>
                <h2 className="mt-4 mb-6 text-navy">Robust <span className="text-blue-600">Developer API.</span></h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-10">
                  Want to build something custom? Our REST API and SDK allow you to extend PMS functionality to fit your team's unique needs.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 text-blue-600 text-left">
                      <Terminal className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Webhooks</h4>
                      <p className="text-sm text-gray-500">Real-time event notifications for your apps.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 text-green-600 text-left">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Secure Auth</h4>
                      <p className="text-sm text-gray-500">OAuth 2.0 and API Key authentication.</p>
                    </div>
                  </div>
                </div>
                <button className="btn-primary mt-10">
                  Read API Docs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* NEW: Integrations FAQ */}
        <section className="shell-white py-24 border-t border-gray-50">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">

            {/* Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

              {/* Left Side - Small Space */}
              <div className="lg:col-span-5">
                <div className="reveal">
                  <span className="section-eyebrow">FAQ</span>
                  <h2 className="mt-4 mb-6 stagger-1">
                    Integration <span className="text-blue-600">FAQs.</span>
                  </h2>
                  <p className="text-lead stagger-2">Find answers to common questions about our integrations,
                    setup process, and technical compatibility.</p>
                </div>
              </div>

              {/* Right Side - Large Space */}
              <div className="lg:col-span-7 space-y-4">
                {integrationFaqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 transition-all hover:border-blue-200 reveal stagger-1"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full px-8 py-6 text-left flex items-center justify-between group"
                    >
                      <span className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {faq.q}
                      </span>
                
                      {openFaq === idx ? (
                        <ChevronUp className="text-blue-600" />
                      ) : (
                        <ChevronDown className="text-gray-400" />
                      )}
                    </button>
                    
                    {openFaq === idx && (
                      <div className="px-8 pb-6 text-gray-600 leading-relaxed animate-fadeIn">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
            </div>
          </div>
        </section>


        {/* Integration Testimonial */}
        <section className="shell-sky py-24 text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 reveal">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl">
                <Activity className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-medium text-navy mb-8 italic leading-snug">
              "The GitHub and Slack integrations changed how we deploy. No more manual updates—PMS handles the state transitions while our team stays focused on code."
            </p>
            <div className="text-gray-700">
              <span className="font-black text-navy uppercase tracking-widest block mb-1">Marcus Thorne</span>
              <span className="text-sm font-bold opacity-60 uppercase">VP of Engineering at CloudScale</span>
            </div>
          </div>
        </section>

        {/* Partner Program CTA */}
        <section className="shell-white py-24 border-t border-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-blue-50 rounded-[40px] p-12 text-center relative overflow-hidden reveal">
              <div className="absolute -top-10 -right-10 opacity-10">
                <HeartHandshake className="w-64 h-64 text-blue-600" />
              </div>
              <div className="relative z-10">
                <h2 className="text-navy mb-6 text-3xl font-black">Become a <span className="text-blue-600">PMS Partner</span></h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
                  Join our ecosystem and build powerful integrations for thousands of global teams. Get technical support and early access to our API roadmap.
                </p>
                <button className="btn-primary bg-navy hover:bg-navy/90">
                  Join Partner Program
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Upcoming Integrations Roadmap */}
        <section className="shell-sky py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="section-eyebrow">Roadmap</span>
              <h2 className="mt-4 text-navy">What's <span className="text-blue-600">Coming Next.</span></h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {upcomingIntegrations.map((item, idx) => (
                <div key={idx} className="bg-white p-6 rounded-3xl border border-white shadow-sm text-center reveal stagger-1 hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-600 shadow-sm">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{item.name}</h4>
                  <div className="flex items-center justify-center gap-1.5 text-xs font-black text-blue-600 uppercase tracking-widest">
                    <Hourglass className="w-3 h-3" />
                    {item.eta}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-white mb-8">Ready to supercharge your workflow?</h2>
            <p className="text-xl text-blue-100 mb-12">
              Join the thousands of teams already connecting their favorite tools with PMS.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Request a Demo
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default IntegrationsPage;
